import { useCallback, useMemo, useState } from "react";

import { CodeExamples } from "../code-block-examples";

import { useBlock } from "@/components/PowerBlock/Block/block-store";
import { LanguageType } from "@/components/CodeEditor/types";
import { CodeExecutionConfiguration } from "@/types/evaluate";

export const useCodeLanguage = () => {
  const blockStore = useBlock<CodeExecutionConfiguration>();

  const [language, setLanguage] = useState<Exclude<LanguageType, "CURL" | "BASH">>(
    (blockStore.configuration?.language as Exclude<LanguageType, "CURL" | "BASH">) || "PYTHON",
  );

  const [code, setCode] = useState<string>(
    blockStore.configuration?.code || CodeExamples[language],
  );

  const isCustomCode = useMemo(() => !CodeExamples[language]?.includes(code), [language, code]);

  const handleLanguageChange = useCallback(
    (newLanguage: LanguageType) => {
      if (newLanguage === "CURL" || newLanguage === "BASH") {
        return;
      }

      setLanguage(newLanguage);
      if (!isCustomCode) {
        setCode(CodeExamples[newLanguage]);
      }

      blockStore.setConfiguration({
        ...blockStore.configuration,
        language: newLanguage.toUpperCase() as LanguageType,
        code: !isCustomCode ? CodeExamples[newLanguage] : code,
      });
    },
    [blockStore, isCustomCode, code],
  );

  const handleCodeChange = useCallback(
    (newCode: string) => {
      setCode(newCode);

      blockStore.setConfiguration({
        ...blockStore.configuration,
        language: language.toUpperCase() as LanguageType,
        code: newCode,
      });
    },
    [blockStore, language],
  );

  const handleExampleChange = useCallback(() => {
    const exampleCode = CodeExamples[language];
    setCode(exampleCode);

    blockStore.setConfiguration({
      ...blockStore.configuration,
      language: language.toUpperCase() as LanguageType,
      code: exampleCode,
    });
  }, [blockStore, language]);

  return {
    language,
    code,
    setCode: handleCodeChange,
    handleLanguageChange,
    handleExampleChange,
    isCustomCode,
  };
};
